import { useMemo } from 'react'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

export interface SocialCountsByType {
  stillImages: number
  carousels: number
  videos: number
  shortFormVideo: number
}

export const useSocialCountsByType = (
  socialCount: number,
  shortFormVideoCount: number,
  stillImageCount: number = 0,
  videoCount: number = 0,
  carouselCount: number = 0,
  options?: {
    percentageOfVisualStylesGeneration?: number
    applyVisualStyle?: boolean
    forceGenV2?: boolean
  },
): SocialCountsByType => {
  const profile = useBrandProfile({ cacheTime: 5 * 60 * 1000 })
  const { data: brandPlan } = useCurrentBrandPlan({ cacheTime: 5 * 60 * 1000 })

  const hasAnimatedImage = !!profile?.data?.brand_kit?.content_preferences?.video_sources?.includes('animated_image')
  const hasStylizedImage =
    !!profile?.data?.brand_kit?.content_preferences?.apply_visual_style || !!options?.applyVisualStyle
  const hasMultipleFeaturedMedia = (profile?.data?.brand_kit?.content_preferences?.featured_media?.length || 0) > 1
  const hasFeaturedImage = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('image')
  const hasFeaturedVideo = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('video')

  const imageWeight = profile?.data?.brand_kit?.content_preferences?.global_format_image_weight ?? 60
  const carouselWeight = profile?.data?.brand_kit?.content_preferences?.global_format_carousel_weight ?? 20
  const videoWeight = profile?.data?.brand_kit?.content_preferences?.global_format_video_weight ?? 20

  const pct = options?.percentageOfVisualStylesGeneration ?? 0.5
  const explicitStillImages = stillImageCount
  const explicitVideos = videoCount
  const explicitCarousels = carouselCount

  const isStrictBrandControl = profile?.data?.brand_kit?.content_preferences?.source_image_edit_level === 'none'
  const isGenV2 = options?.forceGenV2 || (!!brandPlan?.has_content_strategies && !isStrictBrandControl)

  return useMemo((): SocialCountsByType => {
    if (isGenV2) {
      const totalWeight = imageWeight + carouselWeight + videoWeight
      if (totalWeight === 0) {
        return {
          stillImages: explicitStillImages,
          carousels: explicitCarousels,
          videos: explicitVideos,
          shortFormVideo: shortFormVideoCount,
        }
      }

      // Gen V2: distribute socialCount by weights, adjusted for explicit counts already provided.
      // Compute weight-based target for the full total, then fill the deficit from socialCount.
      const totalPosts = socialCount + explicitStillImages + explicitCarousels + explicitVideos
      const targetStill = Math.round((imageWeight / totalWeight) * totalPosts)
      const targetCarousel = Math.round((carouselWeight / totalWeight) * totalPosts)
      const targetVideo = totalPosts - targetStill - targetCarousel

      const needStill = Math.max(0, targetStill - explicitStillImages)
      const needCarousel = Math.max(0, targetCarousel - explicitCarousels)
      const needVideo = Math.max(0, targetVideo - explicitVideos)
      const totalNeed = needStill + needCarousel + needVideo

      let fromSocialStill: number
      let fromSocialCarousel: number

      if (totalNeed === 0) {
        // All types already at or above target; fall back to raw weight distribution for socialCount
        fromSocialStill = Math.round((imageWeight / totalWeight) * socialCount)
        fromSocialCarousel = Math.round((carouselWeight / totalWeight) * socialCount)
      } else {
        const ratio = socialCount / totalNeed
        fromSocialStill = Math.round(needStill * ratio)
        fromSocialCarousel = Math.round(needCarousel * ratio)
      }

      const fromSocialVideo = Math.max(0, socialCount - fromSocialStill - fromSocialCarousel)
      return {
        stillImages: explicitStillImages + fromSocialStill,
        carousels: explicitCarousels + fromSocialCarousel,
        videos: explicitVideos + fromSocialVideo,
        shortFormVideo: shortFormVideoCount,
      }
    }

    // Gen V1: distribute socialCount by featured media type and visual style settings,
    // then add explicit counts on top.
    let distributedStill = 0
    let distributedVideos = 0

    if (hasMultipleFeaturedMedia) {
      if (hasFeaturedImage) distributedStill = Math.round(socialCount * pct)
      if (hasFeaturedVideo) distributedVideos = Math.round(socialCount * pct)
    } else if (hasFeaturedImage && hasStylizedImage) {
      distributedStill = socialCount
    } else if (hasFeaturedVideo && hasAnimatedImage) {
      distributedVideos = socialCount
    } else if (hasStylizedImage) {
      distributedStill = Math.round(socialCount * pct)
    } else {
      distributedStill = socialCount
    }

    return {
      stillImages: distributedStill + explicitStillImages,
      carousels: explicitCarousels,
      videos: distributedVideos + explicitVideos,
      shortFormVideo: shortFormVideoCount,
    }
  }, [
    isGenV2,
    socialCount,
    shortFormVideoCount,
    explicitStillImages,
    explicitCarousels,
    explicitVideos,
    imageWeight,
    carouselWeight,
    videoWeight,
    hasMultipleFeaturedMedia,
    hasFeaturedImage,
    hasFeaturedVideo,
    hasAnimatedImage,
    hasStylizedImage,
    pct,
  ])
}
